import { VIDEO_COPILOT_SOURCE, VideoType } from 'src/constants/constants';

/**
 * Detect video type from URL parameters or schedule details
 */
export function getVideoType(): string {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    if (typeParam) {
      return typeParam.toLowerCase();
    }

    // @ts-ignore - Access global window.player
    const player = window.player;
    if (player) {
      const sources = player.currentSources();
      if (sources && sources.length > 0) {
        const source = sources[0];
        if (source.type && source.type.includes('dash')) return 'penpencilvdo';
        if (source.type && source.type.includes('hls')) return 'awsvideo';
      }
    }

    return 'unknown';
  } catch (error) {
    console.warn('Could not detect video type:', error);
    return 'unknown';
  }
}

/**
 * Extract source type for Video Copilot based on URL parameters
 */
export function extractSourceType({
  scheduleId,
  contentId,
  khazanaProgramId,
  srcType,
  VIDEO_COPILOT_SOURCE,
  isLive,
  ottVideoDetailsId,
  videoType,
}: {
  scheduleId?: string;
  contentId?: string;
  khazanaProgramId?: string;
  srcType?: string;
  VIDEO_COPILOT_SOURCE: any;
  isLive?: boolean;
  ottVideoDetailsId?: string;
  videoType?: string;
}): string {
  if (scheduleId && srcType?.toLowerCase() === VideoType.PENPENCIL_VDO.toLowerCase()) {
    return VIDEO_COPILOT_SOURCE.LECTURE;
  }

  if (scheduleId && (isLive || srcType?.toLowerCase() === VideoType.AWS_VIDEO.toLowerCase())) {
    return VIDEO_COPILOT_SOURCE.LECTURE;
  }

  if (
    ottVideoDetailsId &&
    videoType === VIDEO_COPILOT_SOURCE.OTT &&
    srcType?.toLowerCase() === VideoType.PENPENCIL_VDO.toLowerCase()
  ) {
    return VIDEO_COPILOT_SOURCE.OTT;
  }

  if (
    ottVideoDetailsId &&
    videoType === VIDEO_COPILOT_SOURCE.OTT &&
    (isLive || srcType?.toLowerCase() === VideoType.AWS_VIDEO.toLowerCase())
  ) {
    return VIDEO_COPILOT_SOURCE.OTT;
  }

  if (
    contentId &&
    khazanaProgramId &&
    srcType?.toLowerCase() === VideoType.PENPENCIL_VDO.toLowerCase()
  ) {
    return VIDEO_COPILOT_SOURCE.KHAZANA;
  }

  return '';
}

/**
 * Format time in HH:MM:SS
 */
export function formatTime(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) {
    return '00:00:00';
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const pad = (num: number) => String(num).padStart(2, '0');

  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}

/**
 * Round epoch time to nearest second (remove milliseconds)
 */
function roundEpochToNearestSecond(epochMillis: number): number {
  return Math.floor(epochMillis / 1000) * 1000;
}

/**
 * Format epoch time in UTC format (ISO 8601)
 */
function formatEpochTimeUTC(epochMillis: number): string {
  try {
    if (epochMillis < 0) return '';
    const date = new Date(epochMillis);
    return date.toISOString();
  } catch (e) {
    console.error('Error formatting epoch time:', e);
    return '';
  }
}

/**
 * Determine if video is live based on source type
 *
 * @param sourceType - The video source type (LECTURE, OTT, KHAZANA)
 * @param scheduleDetails - Schedule details object
 * @param ottVideoDetails - OTT video details object
 * @param videoUrlDetails - Video URL details object
 * @returns boolean indicating if the video is live
 */
export function determineIsLive(
  sourceType: string,
  scheduleDetails?: any,
  ottVideoDetails?: any,
  videoUrlDetails?: any,
): boolean {
  let isLiveValue = false;

  if (sourceType === VIDEO_COPILOT_SOURCE.OTT) {
    isLiveValue = ottVideoDetails?.isLive || false;
  } else if (sourceType === VIDEO_COPILOT_SOURCE.LECTURE) {
    isLiveValue = scheduleDetails?.isLive || false;
  }

  const isAwsVideo = videoUrlDetails?.urlType?.toLowerCase() === VideoType.AWS_VIDEO.toLowerCase();
  return isLiveValue || isAwsVideo;
}

/**
 * Calculate current video chunk time for live videos
 * This combines the schedule start time with the current player position
 *
 * @param currentTimeInSeconds - Current player time in seconds
 * @param videoStartTime - Video start time
 * @returns Formatted UTC time string representing the current live video time
 */
export function getCurrentVideoChunkTime(
  currentTimeInSeconds: number,
  videoStartTime: number,
): string {
  try {
    if (!videoStartTime || currentTimeInSeconds < 0) {
      console.warn('⚠️ Invalid parameters for getCurrentVideoChunkTime');
      return '';
    }

    // Detect if videoStartTime is in milliseconds or seconds
    const isMilliseconds = videoStartTime > 32503680000;

    const serverLiveVideoStartTimeMilli = isMilliseconds ? videoStartTime : videoStartTime * 1000;

    if (isNaN(serverLiveVideoStartTimeMilli)) {
      console.error('❌ Invalid schedule start time:', videoStartTime);
      return '';
    }
    const currentPositionInMillis = currentTimeInSeconds * 1000;
    const currentTimeRoundedPosition = roundEpochToNearestSecond(currentPositionInMillis);

    const currentLiveTime = serverLiveVideoStartTimeMilli + currentTimeRoundedPosition;

    const formattedTime = formatEpochTimeUTC(currentLiveTime);
    return formattedTime;
  } catch (error) {
    console.error('❌ Error calculating current video chunk time:', error);
    return '';
  }
}
